import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TripOriginIcon from '@mui/icons-material/TripOrigin';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { actions } from '../../../store/store';
import SkeletonWrapper from '../../../tools/loader/skeletonWrapper';
import InfiniteScrollSentinel from '../../../tools/loader/infiniteScrollSentinel';
import ConfirmDialog from '../../../tools/modal/confirmDialog';
import { useSidebarWidth } from '../../../tools/hooks/useSidebarWidth';
import SidebarResizer from '../../../tools/navs/sidebarResizer';
import { Retract } from '../../../tools/scrollRetract';
import MisDraftStrip, { MIS_DRAFT } from '../misDraftStrip';
import { branchDocDefaults } from '../docSettings';
import { useDocFormat } from '../docFormat';
import PackingListForm from './packingListForm';
import LabelFormForm from './labelFormForm';
import LabelFormDetail from './labelFormDetail';
import { stateColors } from './palletLabelsSection';

const DRAFT_KINDS = [MIS_DRAFT.label.kind];
const PAGE = 40;

// One label form of the list: its number and date, where it goes and from where, who palleted, and how many of its pallets are still free.
const LabelFormCard = memo(function LabelFormCard({ form, selected, onOpen, fmt, colors, T }) {
  const { t } = useTranslation();
  const c = form.counts || { pallets: 0, free: 0, onList: 0, sqm: 0, codes: [] };
  const line = { display: 'flex', alignItems: 'center', gap: 0.3, minWidth: 0 };
  const allOnLists = c.pallets > 0 && c.free === 0;
  const rail = allOnLists ? colors.onList : colors.available;
  return (
    <Box data-zebra-row="" data-lf-row={form.docNumber} onClick={() => onOpen(form)}
      sx={{ position: 'relative', py: 0.9, pl: 1.6, pr: 1.25, borderRadius: '10px', cursor: 'pointer', minWidth: 0,
        border: '1px solid', borderColor: selected ? T.BD2 : 'transparent', bgcolor: selected ? T.CTRL_BG : 'transparent',
        transition: 'background-color 0.12s, border-color 0.12s', '&:hover': { bgcolor: T.CTRL_BG },
        '&::before': { content: '""', position: 'absolute', insetInlineStart: 0, top: 10, bottom: 10, width: '3px', borderRadius: '3px', bgcolor: rail } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: T.TEXT_PRI, flexShrink: 0 }}>#{form.docNumber}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.6, py: '1px', borderRadius: '5px', bgcolor: `${colors.available}1f` }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.available, flexShrink: 0 }} />
          <Typography noWrap data-testid="lf-card-free" sx={{ fontSize: '0.6rem', fontWeight: 700, color: colors.available }}>{t('mis.lfFreeChip', { count: c.free })}</Typography>
        </Box>
        {c.onList > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.6, py: '1px', borderRadius: '5px', bgcolor: `${colors.onList}1f` }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.onList, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.6rem', fontWeight: 700, color: colors.onList }}>{t('mis.lfOnListChip', { count: c.onList })}</Typography>
          </Box>
        )}
        <Typography sx={{ marginInlineStart: 'auto', fontSize: '0.66rem', color: T.TEXT_TER, flexShrink: 0 }}>{fmt.date(form.issueDate, form.dateSystem)}</Typography>
      </Box>
      {(form.destinationAddress || form.originAddress) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.35, minWidth: 0 }}>
          {form.originAddress && (
            <Box sx={{ ...line, flexShrink: 1 }}>
              <TripOriginIcon sx={{ fontSize: 11, color: T.TEXT_TER, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{form.originAddress}</Typography>
            </Box>
          )}
          {form.destinationAddress && (
            <Box sx={{ ...line, flexShrink: 1 }}>
              <PlaceOutlinedIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{form.destinationAddress}</Typography>
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: T.TEXT_PRI, fontVariantNumeric: 'tabular-nums' }}>{t('mis.lfLabelCount', { count: c.pallets })}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, fontVariantNumeric: 'tabular-nums' }}>{Number(c.sqm || 0).toFixed(4)} {t('mis.plColSqm')}</Typography>
        {form.palletizedBy && (
          <Box sx={{ ...line, flexShrink: 1 }}>
            <PersonOutlineIcon sx={{ fontSize: 12, color: T.TEXT_TER, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{form.palletizedBy}</Typography>
          </Box>
        )}
        {c.codes && c.codes.length > 0 && (
          <Typography noWrap dir="ltr" sx={{ marginInlineStart: 'auto', fontSize: '0.66rem', fontFamily: 'monospace', color: T.TEXT_TER, minWidth: 0 }}>
            {c.codes.slice(0, 4).join(' · ')}{c.pallets > 4 ? ' …' : ''}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

// Labels -> Label forms: every label form (batch) of the branch, its record beside the list. New opens the label form; a record opens on its labels
// as they will print. openId: a copied link (/mis?openLabelForm=<id>). newRequest: a counter; a change opens a new form (the Pallets view's New).
export default function LabelFormsSection({ openId, onOpenIdConsumed, switcher = null, newRequest = 0, onNewHandled, onOpenPallet }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMob = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const history = useHistory();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { can } = usePermissions();
  const { activeBranchId, activeBranch } = useBranch();
  const fmt = useDocFormat();

  const canCreate = can('mis:pallet:create') || can('mis:packingList:create');
  const canEdit = can('mis:pallet:edit') || can('mis:packingList:edit');
  const canDelete = can('mis:pallet:delete') || can('mis:packingList:delete');
  const canPrint = can('mis:packingList:pdf') || can('mis:pallet:create') || can('mis:pallet:edit');
  const canList = can('mis:packingList:create');
  const calendar = branchDocDefaults(activeBranch).dateSystem;
  const colors = useMemo(() => stateColors(isDark), [isDark]);

  const T = useMemo(() => ({
    PANEL_BG: isDark ? '#0d0d0d' : theme.palette.background.paper,
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    ROW_ALT:  isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  }), [isDark, theme.palette.background.paper, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [listResizing, setListResizing] = useState(false);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftRefresh, setDraftRefresh] = useState(0);
  const [listPreset, setListPreset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { width: listWidth, setWidth: setListWidth, resetWidth: resetListWidth } = useSidebarWidth('misLabelFormList', 420, { min: 320, max: 640 });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async (skip = 0) => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const res = await authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms`,
        params: { branchId: activeBranchId, limit: PAGE, skip, ...(debounced.trim() ? { search: debounced.trim() } : {}) },
      });
      setRows((cur) => (skip ? [...cur, ...res.data.data] : res.data.data));
      setTotal(res.data.total);
    } catch (_) {
      dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lfLoadFailed'), type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, authCtx, axiosGlobal, debounced, dispatch, t]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => { setSelectedId(null); setMobileDetail(false); }, [activeBranchId]);

  useEffect(() => {
    if (!openId) return;
    setSelectedId(openId);
    if (isMob) setMobileDetail(true);
    if (onOpenIdConsumed) onOpenIdConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  // the Pallets view's New: a label form, here
  useEffect(() => {
    if (!newRequest) return;
    if (canCreate) { setEditing(null); setDraft(null); setFormOpen(true); }
    if (onNewHandled) onNewHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRequest]);

  const open = useCallback((form) => { setSelectedId(form._id); if (isMob) setMobileDetail(true); }, [isMob]);
  const back = () => { setSelectedId(null); setMobileDetail(false); };

  const closeForm = (result) => {
    setFormOpen(false); setEditing(null); setDraft(null);
    setDraftRefresh((n) => n + 1);
    if (!result || !result.form) return;
    load(0);
    setDetailRefresh((n) => n + 1);
    open(result.form);
  };

  const remove = async () => {
    const form = confirmDelete;
    setConfirmDelete(null);
    try {
      await authCtx.jwtInst({ method: 'delete', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/${form._id}` });
      if (selectedId === form._id) back();
      dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lfDeleted', { number: form.docNumber }), type: 'success' }));
      load(0);
    } catch (_) {
      dispatch(actions.setShowSnackBar({ status: true, msg: t('mis.lfDeleteFailed'), type: 'error' }));
      load(0);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>

      {(!isMob || !mobileDetail) && (
        <Box sx={{ width: isMob ? '100%' : (selectedId ? listWidth : '100%'), flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: (!isMob && selectedId) ? `1px solid ${T.BD}` : 'none', bgcolor: T.PANEL_BG, overflow: 'hidden', position: 'relative',
          transition: listResizing ? 'none' : 'width 0.2s' }}>
          {!isMob && selectedId && (
            <SidebarResizer width={listWidth} side="right" onResize={(w) => { setListResizing(true); setListWidth(w); }} onDoubleClick={resetListWidth} />
          )}

          {/* the switch, search and New fold away with the MIS top bar while the list scrolls down */}
          <Retract>
          <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
            {switcher}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, minWidth: 140, bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}`, borderRadius: '9px', px: 1, height: isMob ? 40 : 30 }}>
              <SearchIcon sx={{ fontSize: 15, color: T.TEXT_TER }} />
              <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('mis.lfSearchPlaceholder')}
                inputProps={{ 'aria-label': t('mis.lfSearchPlaceholder'), 'data-testid': 'lf-search' }}
                sx={{ fontSize: isMob ? '16px' : '0.78rem', color: T.TEXT_PRI, flex: 1, '& input::placeholder': { color: T.TEXT_TER, opacity: 1 } }} />
              {search && (
                <IconButton size="small" onClick={() => setSearch('')} aria-label={t('common.close')} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 13, color: T.TEXT_TER }} />
                </IconButton>
              )}
            </Box>
            {canCreate && (
              <Button variant="contained" size="small" onClick={() => { setEditing(null); setDraft(null); setFormOpen(true); }} data-testid="lf-new"
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                sx={{ height: isMob ? 40 : 30, borderRadius: '8px', fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, flexShrink: 0 }}>
                {t('mis.lfNewTitle')}
              </Button>
            )}
          </Box>
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
            <Typography data-testid="lf-count" sx={{ fontSize: '0.72rem', color: T.TEXT_TER }}>{t('mis.lfCount', { count: total })}</Typography>
          </Box>
          </Retract>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 0.75 }}>
            {canCreate && (
              <MisDraftStrip kinds={DRAFT_KINDS} branchId={activeBranchId} T={T} refreshKey={draftRefresh}
                onOpen={(d) => { setEditing(null); setDraft(d); setFormOpen(true); }} />
            )}
            <SkeletonWrapper loading={loading && rows.length === 0} variant="table" count={6}>
              {rows.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, px: 3, gap: 1 }}>
                  <LibraryBooksOutlinedIcon sx={{ fontSize: 30, color: T.TEXT_TER }} />
                  <Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC, textAlign: 'center' }}>{search ? t('mis.lfNoneMatch') : t('mis.lfNoneYet')}</Typography>
                </Box>
              ) : (
                <Box sx={{ px: 1, py: 0.5, display: 'grid', gap: 0.75, gridTemplateColumns: !isMob && !selectedId ? 'repeat(auto-fill, minmax(380px, 1fr))' : 'minmax(0, 1fr)' }}>
                  {rows.map((form) => (
                    <LabelFormCard key={form._id} form={form} selected={form._id === selectedId} onOpen={open} fmt={fmt} colors={colors} T={T} />
                  ))}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <InfiniteScrollSentinel onIntersect={() => load(rows.length)} hasMore={rows.length < total} loading={loading} />
                  </Box>
                </Box>
              )}
            </SkeletonWrapper>
          </Box>
        </Box>
      )}

      {selectedId && (!isMob || mobileDetail) && (
        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', bgcolor: T.PANEL_BG }}>
          <LabelFormDetail formId={selectedId} refreshKey={detailRefresh} canPrint={canPrint} canEdit={canEdit} canDelete={canDelete} canList={canList}
            colors={colors} calendar={calendar} onBack={back}
            onEdit={(form) => { setEditing(form); setDraft(null); setFormOpen(true); }}
            onDelete={setConfirmDelete}
            onMakeList={(form, free) => setListPreset({ pallets: free, originAddress: form.originAddress || '', destinationAddress: form.destinationAddress || '' })}
            onOpenPallet={onOpenPallet}
            onOpenList={(id) => history.push(`/mis?openPackingList=${id}`)} T={T} />
        </Box>
      )}

      {(canCreate || canEdit) && <LabelFormForm open={formOpen} form={editing} draft={draft} onClose={closeForm} />}
      {canList && (
        <PackingListForm open={Boolean(listPreset)} preset={listPreset}
          onClose={() => { setListPreset(null); load(0); setDetailRefresh((n) => n + 1); }} />
      )}
      <ConfirmDialog open={Boolean(confirmDelete)} destructive title={t('mis.lfDeleteTitle')}
        message={confirmDelete ? t('mis.lfDeleteBody', { number: confirmDelete.docNumber }) : ''}
        confirmLabel={t('common.delete')} cancelLabel={t('common.cancel')} onConfirm={remove} onClose={() => setConfirmDelete(null)} />
    </Box>
  );
}
